import type { ChallengeDetail } from "../../challenges/types/challengeAdmin";
import type { DraftPair, DraftPlayer } from "../../challenges/types/challengeDraft";
import { normalizePocoCourtsCount } from "../../challenges/utils/pocoSchedule";
import { isFixedPairOnlyChallengeType, minimumParticipantsByType } from "./adminChallengeEditor";

type AdminChallengeStartStateParams = {
  challengeDetail: ChallengeDetail | null;
  challengeSelectedParticipantsCount: number;
  draftPairs: DraftPair[];
  draftPlayers: DraftPlayer[];
  isPoco: boolean;
  isSuper8: boolean;
  super8PlayersForCurrentChallenge: DraftPlayer[];
};

function resolveMaxDrawPlayersForCurrentChallenge(
  challengeDetail: ChallengeDetail | null,
) {
  const challengeType = String(challengeDetail?.challengeType || "")
    .trim()
    .toLowerCase();
  if (challengeType === "super8") return 8;
  if (challengeType !== "challenge") return null;
  if (challengeDetail?.challengeModel === "custom") {
    const configuredPlayersCount = Math.floor(
      Number(challengeDetail.playersCount || 12),
    );
    return Number.isFinite(configuredPlayersCount) && configuredPlayersCount > 0
      ? configuredPlayersCount
      : 12;
  }
  return 12;
}

export function getAdminChallengeStartState({
  challengeDetail,
  challengeSelectedParticipantsCount,
  draftPairs,
  draftPlayers,
  isPoco,
  isSuper8,
  super8PlayersForCurrentChallenge,
}: AdminChallengeStartStateParams) {
  const configuredPocoPlayers =
    challengeDetail?.challengeType === "poco"
      ? normalizePocoCourtsCount(challengeDetail.courtsCount) * 4
      : 0;
  const minimumParticipants = challengeDetail
    ? challengeDetail.challengeType === "poco" ||
      (challengeDetail.challengeModel === "custom" &&
        challengeDetail.challengeType !== "super8")
      ? Math.max(
          minimumParticipantsByType(challengeDetail.challengeType),
          Math.floor(
            Number(
              challengeDetail.playersCount ||
                configuredPocoPlayers ||
                minimumParticipantsByType(challengeDetail.challengeType),
            ),
          ),
        )
      : minimumParticipantsByType(challengeDetail.challengeType)
    : 0;
  const maxFixedPairsForCurrentChallenge =
    challengeDetail &&
    isFixedPairOnlyChallengeType(challengeDetail.challengeType)
      ? Math.floor(minimumParticipants / 2)
      : null;
  const maxDrawPlayersForCurrentChallenge =
    resolveMaxDrawPlayersForCurrentChallenge(challengeDetail);
  const displayedMinimumParticipants =
    minimumParticipants;
  const isTournamentType =
    String(challengeDetail?.challengeType || "")
      .trim()
      .toLowerCase() === "tournament";
  const canStartChallenge =
    !!challengeDetail &&
    challengeDetail.status === "pending" &&
    (maxDrawPlayersForCurrentChallenge === null ||
      challengeSelectedParticipantsCount <= maxDrawPlayersForCurrentChallenge) &&
    (isTournamentType ||
      (challengeSelectedParticipantsCount % 2 === 0 &&
        (challengeDetail.challengeType === "super8"
            ? challengeSelectedParticipantsCount === 8
          : isPoco
            ? challengeSelectedParticipantsCount === minimumParticipants
            : challengeSelectedParticipantsCount >= minimumParticipants)));
  const canFinishChallenge =
    !!challengeDetail && challengeDetail.status === "started";
  const canGenerateFixedPairRounds =
    draftPairs.length >= 2 &&
    draftPairs.length * 2 >= displayedMinimumParticipants &&
    (maxFixedPairsForCurrentChallenge === null ||
      draftPairs.length <= maxFixedPairsForCurrentChallenge);
  const canGeneratePlayerBasedRounds = isSuper8
    ? draftPlayers.length === 8 && super8PlayersForCurrentChallenge.length === 8
    : draftPlayers.length >= displayedMinimumParticipants;
  const canGeneratePlayerBasedRoundsWithinLimit =
    canGeneratePlayerBasedRounds &&
    (maxDrawPlayersForCurrentChallenge === null ||
      draftPlayers.length <= maxDrawPlayersForCurrentChallenge);

  return {
    canFinishChallenge,
    canGenerateFixedPairRounds,
    canGeneratePlayerBasedRounds: canGeneratePlayerBasedRoundsWithinLimit,
    canStartChallenge,
    maxFixedPairsForCurrentChallenge,
    maxDrawPlayersForCurrentChallenge,
    minimumParticipants,
  };
}
